import React from "react";
import { useEffect } from "react";
import Box from "@mui/material/Box";
import Plot from "react-plotly.js";
import WebFont from "webfontloader";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";

import MyCountUp from "../UtilityComponents/NumberDisplayComponents/MyCountUp";
import { Divider, LinearProgress } from "@mui/material";

import OverviewOptions from "./OverviewOptions";
import OverviewPlotPaperProps from "./OverviewPlotPaperProps";
import periodToXAxisRange from "../UtilityComponents/GraphOptionUtilityComponents/periodToXAxisRange";
import AsOf from "../UtilityComponents/PaperUtilityComponents/AsOf";

import { useTheme } from "@mui/material/styles";

const paperPadding = 2;
const summaryStatPaddingTopBottom = 1;
const enterToolTipDelay = 400;

export type PlotJsonType = { [key: string]: any };

function OverviewSimplePaper(props: OverviewPlotPaperProps) {
  const theme = useTheme();
  useEffect(() => {
    WebFont.load({
      google: {
        families: ["Roboto"],
      },
    });
  }, []);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", width: "100%" }}>
      <Box
        className="highlight-for-overview-tour-step-1"
        sx={{
          // height: percentOfPaperHeightForSummaryStats, // TODO: set a min height
          py: summaryStatPaddingTopBottom,
          flexGrow: 0,
          flexDirection: "column",
          width: "100%",
          display: "flex",
          sizing: "border-box",
          justifyContent: "space-evenly",
          overflowX: "auto",
          gap: 2,
        }}
      >
        {[
          {
            description: "is the total value of all your stock!",
            value: (
              <MyCountUp
                value={props.totalStockValue}
                lastFullyDrawnValue={props.lastDrawnTotalStockValue}
                setLastFullyDrawnValue={props.setLastDrawnTotalStockValue}
                prefix="$"
                negativeIsError
                error={props.statsError}
              />
            ),
            tooltip_text:
              "This is the total stock value of all grants with current market conditions after accounting for any sold shares.",
          },
          {
            description: "is how much stock you have and can access today!",
            value: (
              <MyCountUp
                value={props.vestedValue}
                lastFullyDrawnValue={props.lastDrawnVestedValue}
                setLastFullyDrawnValue={props.setLastDrawnVestedValue}
                prefix="$"
                negativeIsError
                error={props.statsError}
              />
            ),
            tooltip_text: "This is the total stock value of grants that have already vested today.",
          },
          {
            description: "of your equity is vested and available to you",
            value: (
              <Box alignItems="center" sx={{ display: "flex", flexDirection: "row", width: 150 }}>
                <Box sx={{ width: "100%", mr: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={props.pctVested * 100}
                    sx={{
                      width: "100%",
                      height: 30,
                      border: 0.5,
                      borderRadius: 1,
                      borderColor: props.pctVested > 0.99 ? theme.palette.success.light : theme.palette.info.light,
                      backgroundColor: theme.palette.background.default,
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: props.pctVested > 0.99 ? theme.palette.success.main : theme.palette.info.main,
                      },
                    }}
                  />
                </Box>
                <MyCountUp
                  value={props.pctVested * 100}
                  lastFullyDrawnValue={props.lastDrawnPctVested}
                  setLastFullyDrawnValue={props.setLastDrawnPctVested}
                  suffix="%"
                  negativeIsError
                  error={props.statsError}
                />
              </Box>
            ),
            tooltip_text: "This is the percentage of your shares that have vested.",
          },
          {
            description: "is the current " + (props.userSymbol ? props.userSymbol + " " : "") + "Stock Price",
            value: (
              <MyCountUp
                value={props.referencePrice}
                lastFullyDrawnValue={props.lastDrawnReferencePrice}
                setLastFullyDrawnValue={props.setLastDrawnReferencePrice}
                prefix="$"
                decimals={2}
                negativeIsError
                error={props.statsError}
              />
            ),
            tooltip_text: "This is the last close price for the referenced stock in the stock plans.",
          },
        ].map((label, index) => (
          <Tooltip
            key={index}
            title={label.tooltip_text}
            placement="bottom-start"
            arrow
            enterDelay={enterToolTipDelay}
            sx={{ flexGrow: 1 }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "flex-start",
                alignItems: "flex-end",
                sizing: "border-box",
                px: 1,
              }}
            >
              <Typography variant="h4" noWrap sx={{ paddingRight: 1 }}>
                {props.plotLoading ? <Skeleton /> : label.value}
              </Typography>
              <Typography variant="h6" noWrap sx={{}}>
                {label.description}
              </Typography>
            </Box>
          </Tooltip>
        ))}
      </Box>
      <AsOf referenceDateString={props.referenceDateString} />
    </Box>
  );
}

export default OverviewSimplePaper;
